import { Stack } from '@mui/material';
import MonitorMgSupplierItemsDialog from './MonitorMgSupplierItemsDialog';
import { useAssortmentItemsActions } from '@retail/my-assortment/context';
import { usePermissionsForCurrentUser } from '@retail/app/stores/selected-context';
import { ReactNode, useCallback, useState } from 'react';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { useNavigate } from 'react-router';
import { useDisclosure } from '@shared/hooks';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import { MgSupplierItemSearchResult, ProductGroupBase } from '@retail/products/types';
import { MgSupplierItemSearchContainer } from './MgSupplierItemSearchContainer';
import { useMonitoringAssortments } from '@retail/monitoring-assortment/context';

interface Props {
  assortmentId: number;
  renderAddItemsDialog: (props: {
    selectedItems: AssortmentItemMinimal[];
    onClose: () => void;
  }) => ReactNode;
}

export function MgSupplierItemSearchPageContainer({ assortmentId, renderAddItemsDialog }: Props) {
  const navigate = useNavigate();

  const { monitoringAssortments } = useMonitoringAssortments();

  const { updateItemsIsLoading } = useAssortmentItemsActions();

  const { userCanCreate } = usePermissionsForCurrentUser();

  const canAddAssortmentItems = userCanCreate('ASSORTMENT_ITEMS');
  const canAddMonitoringItems = userCanCreate('MONITORING_ASSORTMENT_ITEMS');

  const navigateToAssortment = useCallback(
    (assortmentId: number) => {
      navigate(mgPrisRoutes.marketInsight.assortments.assortment.getFullLinkPath({ assortmentId }));
    },
    [navigate]
  );
  const navigateToMgItem = useCallback(
    (mgItemNumber: string) => {
      navigate(mgPrisRoutes.items.item.getFullLinkPath({ itemNumber: mgItemNumber }));
    },
    [navigate]
  );

  const { isOpen: editItemsModalOpen, onToggle: toggleEditItemsModal } = useDisclosure();
  const {
    isOpen: isMonitorItemsModalOpen,
    onOpen: _onOpenMonitorItemsModal,
    onClose: onCloseMonitorItemsModal,
  } = useDisclosure(false);

  const onOpenAssortmentItemsModal = !canAddAssortmentItems ? undefined : toggleEditItemsModal;
  const onOpenMonitorItemsModal = !canAddMonitoringItems ? undefined : _onOpenMonitorItemsModal;

  const [checkedItems, setCheckedItems] = useState<MgSupplierItemSearchResult[]>([]);

  return (
    <Stack flexGrow={1} overflow="hidden">
      <MgSupplierItemSearchContainer
        includeHeader
        assortmentId={assortmentId}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        navigateToMgItem={navigateToMgItem}
        updateItemsIsLoading={updateItemsIsLoading}
        onOpenAssortmentItemsModal={onOpenAssortmentItemsModal}
        onOpenMonitorItemsModal={onOpenMonitorItemsModal}
      />

      {isMonitorItemsModalOpen && monitoringAssortments && (
        <MonitorMgSupplierItemsDialog
          open
          onClose={onCloseMonitorItemsModal}
          selectedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          monitoringAssortments={monitoringAssortments}
          navigateToAssortment={navigateToAssortment}
        />
      )}
      {editItemsModalOpen &&
        canAddAssortmentItems &&
        renderAddItemsDialog({
          selectedItems: checkedItems.map((item) => {
            return {
              ...item,
              supplier: { id: item.mgSupplierId, name: item.participantAccountName },
              productGroup: item.bk04Varegruppe
                ? ProductGroupBase.fromDto({
                    name: item.bk04Varegruppe?.name,
                    groupNumber: item.bk04Varegruppe?.groupNumber,
                  })
                : undefined,
            };
          }) as AssortmentItemMinimal[],
          onClose: () => {
            setCheckedItems([]);
            toggleEditItemsModal();
          },
        })}
    </Stack>
  );
}
